import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, catchError, filter, from, of, switchMap, take, tap, timer, timeout } from 'rxjs';
import { Store } from '@ngrx/store';

import { AuthenticationService, ConfigurationService, B2CLoginService, SpinnerService } from '../_shared/services/services-index';
import { AppState } from '../_store/app-state.model';
import { AppActions } from '../_store/action-types';
import { AppSelectors } from '../_store/selector-types';
import { FilterActions } from '../_shared/filter/store';
import { environment } from '../../environments/environment';
import { IPublicClientApplication } from '@azure/msal-browser';
import { MSAL_B2C_INSTANCE } from '../msal-instances';

@Component({
  selector: 'app-b2c-callback',
  template: `
    <div class="text-center my-5">
      <h3>Processing B2C login...</h3>
      <div *ngIf="errorMessage" class="text-red-600 mt-4">
        {{ errorMessage }}
      </div>
      <div *ngIf="isDebugMode" class="mt-4 text-xs bg-gray-100 p-4 rounded-md text-left max-w-lg mx-auto">
        <h4 class="font-bold">Debug Information</h4>
        <div class="mt-2">
          <pre class="whitespace-pre-wrap text-xs">{{ debugInfo | json }}</pre>
        </div>
      </div>
      <div class="mt-4">
        <button *ngIf="errorMessage" class="btn btn-primary" (click)="retryLogin()">Retry Login</button>
        <button class="btn btn-link ml-2" (click)="backToLogin()">Back to Login</button>
      </div>
    </div>
  `
})
export class B2CCallbackComponent implements OnInit, OnDestroy {
  errorMessage: string = '';
  isDebugMode = !environment.production;
  debugInfo: any = {
    loginSteps: []
  };
  retryCount = 0;
  maxRetries = 2;
  private subscription = new Subscription();

  constructor(
    @Inject(MSAL_B2C_INSTANCE) private msalB2CInstance: IPublicClientApplication,
    private b2cLoginService: B2CLoginService,
    private authService: AuthenticationService,
    private router: Router,
    private spinner: SpinnerService,
    private configService: ConfigurationService,
    private store$: Store<AppState>
  ) {}

  /**
   * Add a step to the debug log with timestamp
   */
  private addDebugStep(step: string): void {
    if (this.isDebugMode) {
      this.debugInfo.loginSteps.push({
        step,
        timestamp: new Date().toISOString()
      });
    }
  }

  ngOnInit(): void {
    this.spinner.show();
    console.log('[B2CCallbackComponent] Initializing and processing redirect...');
    this.addDebugStep('Initializing and processing redirect');

    // Get accounts at startup to see if we're already logged in
    const accounts = this.msalB2CInstance.getAllAccounts();
    if (accounts.length > 0) {
      console.log('[B2CCallbackComponent] Found existing account at initialization:', accounts[0].name);
      this.addDebugStep(`Found existing account: ${accounts[0].name}`);
      this.debugInfo.existingAccount = accounts[0].name;
    }
    
    // Add timeout to bail out if token exchange takes too long
    const timeoutSub = timer(30000).subscribe(() => {
      if (!this.errorMessage) {
        this.errorMessage = 'Login process timed out. Please try again.';
        this.debugInfo.timeoutError = true;
        this.addDebugStep('Login process timed out after 30 seconds');
        this.spinner.hide();
      }
    });
    this.subscription.add(timeoutSub);
    
    // Listen for MSAL login events in parallel with our direct handling
    const loginSub = this.b2cLoginService.onLoginSuccess$().subscribe(event => {
      console.log('[B2CCallbackComponent] Detected login success event:', event.eventType);
      this.addDebugStep(`Detected login success event: ${event.eventType}`);
      this.debugInfo.loginSuccessEvent = true;
      this.debugInfo.loginSuccessEventType = event.eventType;
      this.debugInfo.loginSuccessEventTimestamp = new Date().toISOString();
      
      // Check if we have an account after the success event
      const accounts = this.msalB2CInstance.getAllAccounts();
      if (accounts.length > 0) {
        console.log('[B2CCallbackComponent] Account available after event detection:', accounts[0].name);
        this.addDebugStep(`Account available after event: ${accounts[0].name}`);
      }
    });
    this.subscription.add(loginSub);
    
    this.processRedirect();
  }

  /**
   * Process the MSAL redirect response
   */
  private processRedirect(): void {
    this.addDebugStep('Starting redirect handling with B2C callback');
    console.log('[B2CCallbackComponent] Starting B2C redirect handling...');
    
    const sub = this.b2cLoginService.onRedirectComplete$()
      .pipe(
        tap(() => {
          this.debugInfo.redirectHandlingStarted = true;
          this.debugInfo.timestamp = new Date().toISOString();
          this.addDebugStep('Redirect handling started');
        }),
        catchError(error => {
          // Handle error in the redirect promise
          console.error('[B2CCallbackComponent] Error in redirect handling:', error);
          this.addDebugStep(`Error in redirect handling: ${error.message}`);
          
          // Check if we have accounts despite the error
          const accounts = this.msalB2CInstance.getAllAccounts();
          if (accounts.length > 0) {
            this.addDebugStep(`Found account despite redirect error: ${accounts[0].name}`);
            console.log('[B2CCallbackComponent] Found account despite redirect error:', accounts[0].name);
            
            // Try to acquire token silently for this account
            return this.b2cLoginService.acquireTokenSilent$().pipe(
              catchError(silentError => {
                console.error('[B2CCallbackComponent] Silent token acquisition failed:', silentError);
                this.addDebugStep(`Silent token acquisition failed: ${silentError.message}`);
                throw error; // throw original error if silent acquisition fails
              })
            );
          }
          
          // Retry logic for redirect handling
          if (this.retryCount < this.maxRetries) {
            this.retryCount++;
            this.addDebugStep(`Retrying redirect handling (attempt ${this.retryCount} of ${this.maxRetries})`);
            // Wait a bit and retry
            setTimeout(() => this.processRedirect(), 1000);
            return of(null);
          }
          
          this.errorMessage = `MSAL redirect handling failed: ${error.message}`;
          this.spinner.hide();
          return of(null);
        }),
        filter(authResult => !!authResult), // Filter out null results from error handling
        tap(authResult => {
          console.log('[B2CCallbackComponent] Auth result received', authResult);
          this.addDebugStep('Auth result received from MSAL');
          
          if (this.isDebugMode) {
            this.debugInfo.authResultReceived = true;
            this.debugInfo.authResultTimestamp = new Date().toISOString();
            this.debugInfo.userAccountName = authResult.account?.name;
            this.debugInfo.tokenType = authResult.tokenType;
            this.debugInfo.hasIdToken = !!authResult.idToken;
          }
        }),
        switchMap(authResult => {
          if (!authResult.idToken) {
            console.error('[B2CCallbackComponent] No ID token in auth result');
            this.addDebugStep('No ID token in auth result');
            if (this.isDebugMode) {
              this.debugInfo.noIdToken = true;
            }
            throw new Error('No ID token received from B2C');
          }
          
          this.addDebugStep('ID token received, exchanging for app token');
          return this.authService.exchangeB2CToken(authResult.idToken).pipe(
            catchError(error => {
              console.error('[B2CCallbackComponent] Token exchange failed', error);
              this.addDebugStep(`Token exchange failed: ${error.message || 'Unknown error'}`);
              if (this.isDebugMode) {
                this.debugInfo.tokenExchangeError = error.message || 'Unknown error during token exchange';
                this.debugInfo.tokenExchangeErrorStatus = error.status;
                this.debugInfo.tokenExchangeErrorTimestamp = new Date().toISOString();
              }
              throw error;
            })
          );
        }),
        // unifiedTokenResponse: { accessToken, refreshToken, expiresIn }
        tap(unifiedTokenResponse => {
          console.log('[B2CCallbackComponent] Token exchanged successfully', unifiedTokenResponse);
          if (this.isDebugMode) {
            this.debugInfo.tokenExchangedSuccessfully = true;
            this.debugInfo.tokenExchangeTimestamp = new Date().toISOString();
            this.debugInfo.expiresIn = unifiedTokenResponse.expiresIn;
          }
        }),
        switchMap(unifiedTokenResponse => {
          return this.authService.loginCustomToken(unifiedTokenResponse.accessToken, unifiedTokenResponse.refreshToken).pipe(
            catchError(error => {
              console.error('[B2CCallbackComponent] Custom token login failed', error);
              if (this.isDebugMode) {
                this.debugInfo.customTokenLoginError = error.message || 'Unknown error during custom token login';
                this.debugInfo.customTokenLoginErrorTimestamp = new Date().toISOString();
              }
              throw error;
            })
          );
        }),
        tap(() => {
          console.log('[B2CCallbackComponent] Custom token login completed, waiting for store update');
          this.addDebugStep('Custom token login completed, waiting for store update');
        }),
        // Use a retry mechanism with delay to check for the user in the store
        switchMap(() => {
          console.log('[B2CCallbackComponent] Checking for user in store...');
          this.addDebugStep('Checking for user in store');
          
          // Create a stream that checks for the user multiple times with a delay between attempts
          return timer(0, 500).pipe( // Check immediately, then every 500ms
            tap(counter => {
              console.log(`[B2CCallbackComponent] User store check attempt ${counter + 1}`);
              this.addDebugStep(`User store check attempt ${counter + 1}`);
              if (this.isDebugMode) {
                this.debugInfo.userStoreCheckAttempt = counter + 1;
              }
            }),
            switchMap(() => this.store$.select(AppSelectors.selectLoggedInUser)),
            tap(user => {
              console.log('[B2CCallbackComponent] User in store check:', user ? 'found' : 'not found yet');
              if (user) {
                this.addDebugStep('User found in store');
                if (this.isDebugMode) {
                  this.debugInfo.userFoundInStore = true;
                  this.debugInfo.userFoundTimestamp = new Date().toISOString();
                }
              }
            }),
            // Take the first non-null user or stop after 10 attempts (5 seconds)
            filter(user => !!user),
            take(1),
            timeout(5000), // Fail after 5 seconds of trying
            catchError(error => {
              console.error('[B2CCallbackComponent] Timed out waiting for user in store', error);
              this.addDebugStep('Timed out waiting for user in store');
              if (this.isDebugMode) {
                this.debugInfo.userStoreTimeoutError = true;
              }
              throw new Error('Login succeeded but user data was not available in store within the timeout period');
            })
          );
        }),
        // Now we should definitely have the user
        tap(user => {
          if (!user) {
            console.error('[B2CCallbackComponent] No user after login and retries');
            this.addDebugStep('No user after login and retries');
            if (this.isDebugMode) {
              this.debugInfo.noUserAfterLogin = true;
            }
            throw new Error('Login succeeded but no user data was retrieved after retries');
          }
        }),
        tap(user => {
          console.log('[B2CCallbackComponent] Login completed successfully', user);
          if (this.isDebugMode) {
            this.debugInfo.loginSuccessful = true;
            this.debugInfo.loginTimestamp = new Date().toISOString();
            this.debugInfo.userName = user.userName;
            this.debugInfo.userRole = user.role;
          }
          // Reset all report view filters when login is successful
          this.store$.dispatch(FilterActions.resetAllReportViewFilters());
        }),
        tap(() => {
          this.spinner.hide();
          this.router.navigate([this.configService.routing.defaultRoute]);
        }),
        catchError(err => {
          console.error('[B2CCallbackComponent] Login process error', err);
          this.spinner.hide();
          this.addDebugStep(`Login process failed: ${err.message || 'Unknown error'}`);
          
          // Create a more informative error message
          let errorDetails = '';
          if (err.errorCode) {
            errorDetails = `[${err.errorCode}] `;
          }
          errorDetails += err.message || 'Unknown error';
          
          // Provide guidance based on error type
          if (err.message?.includes('interaction_in_progress')) {
            errorDetails += '. Try clearing your browser cache and cookies.';
          } else if (err.message?.includes('token')) {
            errorDetails += '. There may be an issue with the authentication configuration.';
          } else if (err.message?.includes('AADB2C')) {
            errorDetails += '. There may be an issue with the B2C configuration.';
          }
          
          this.errorMessage = `B2C Login failed: ${errorDetails}`;
          
          if (this.isDebugMode) {
            this.debugInfo.loginError = err.message || 'Unknown error';
            this.debugInfo.errorCode = err.errorCode;
            this.debugInfo.loginErrorStack = err.stack;
            this.debugInfo.errorTimestamp = new Date().toISOString();
            this.debugInfo.msalInstance = this.msalB2CInstance ? 'Available' : 'Not available';
            // Add potentially helpful debugging info
            try {
              this.debugInfo.accounts = this.msalB2CInstance?.getAllAccounts()?.length || 0;
            } catch (e) {
              this.debugInfo.accountsError = 'Failed to get accounts';
            }
          }
          
          return of(null); // Don't navigate away, show the error on this page
        })
      ).subscribe();

    this.subscription.add(sub);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.spinner.hide();
  }
  
  /**
   * Retry the login process
   */
  retryLogin() {
    this.errorMessage = '';
    this.spinner.show();
    this.router.navigate(['/login']).then(() => {
      this.b2cLoginService.loginRedirect();
    });
  }
  
  /**
   * Navigate back to the login page
   */
  backToLogin() {
    this.router.navigate(['/login']);
  }
}
